import React, { useEffect, useState } from 'react';
import './style.scss';
import PropTypes from 'prop-types';
import {
  closestCorners,
  DndContext,
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  PointerSensor,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { addOrderForList } from 'utils/func';
import SortableItem from './SortableItem';

class CustomPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: () => false,
    },
  ];
}

function SortableList({ itemTemplate, items, replaceItems, disabled }) {
  const [listItems, setListItems] = useState([]);

  useEffect(() => {
    setListItems(items);
  }, [items]);

  const handleDragEnd = (e) => {
    const { active, over } = e;

    if (active?.id === undefined || over?.id === undefined || active?.id === over?.id) {
      return;
    }

    setListItems((prevItems) => {
      const oldIndex = prevItems.findIndex((item) => item.id === active.id);
      const newIndex = prevItems.findIndex((item) => item.id === over.id);
      const newItems = addOrderForList(arrayMove(listItems, oldIndex, newIndex));

      replaceItems(newItems);

      return newItems;
    });
  };

  const sensors = useSensors(
    useSensor(CustomPointerSensor, {
      activationConstraint: {
        delay: 100,
      },
    }),
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  return (
    <tbody>
      <tr>
        <td colSpan="100%">
          <DndContext
            collisionDetection={closestCorners}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            accessibility={{
              screenReaderInstructions: {
                draggable: '',
              },
            }}
          >
            <table>
              <tbody>
                <SortableContext strategy={verticalListSortingStrategy} items={listItems}>
                  {listItems?.length > 0 &&
                    listItems.map((item, index) => (
                      <SortableItem
                        key={`sortable-list-${item.id}`}
                        item={item}
                        index={index}
                        itemTemplate={itemTemplate}
                        disabled={disabled}
                      />
                    ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </td>
      </tr>
    </tbody>
  );
}

export default React.memo(SortableList);

SortableList.propTypes = {
  itemTemplate: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  replaceItems: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SortableList.defaultProps = {
  disabled: false,
};
